import React, { ButtonHTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react';
import { useBearer } from './_admin-utils';
import {
  creditValueCentsToCredits,
  creditValueCentsToUsd,
  formatCreditsValue,
  formatUsd,
} from '@/utils/credits';
import { MarketItemsPanel } from '@/components/admin/MarketItemsPanel';

interface CatalogItem {
  id?: string;
  name: string;
  active?: boolean;
  usd_price?: number;
  credit_value_cents?: number;
  cost_cents?: number;
  image_url?: string;
  theme?: string;
  tier?: string;
  description?: string;
}

interface LedgerEntry {
  id: string;
  type: string;
  delta_credits?: number;
  created_at: string;
  user_id?: string;
  account_id?: string;
  note?: string;
}

interface TabButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
}

interface EditDrawerProps {
  sub: string;
  item: CatalogItem;
  onClose: () => void;
  onSave: (row: CatalogItem) => void;
}

interface GiftPanelProps {
  bearer: Record<string, string> | null;
}

function TabButton({ active, children, ...props }: TabButtonProps) {
  return (
    <button
      {...props}
      className={`px-3 py-2 rounded-lg transition ${
        active ? 'bg-terracotta text-white shadow' : 'bg-sand text-brown-700 hover:bg-sand/80'
      }`}
    >
      {children}
    </button>
  );
}

function formatCredits(value: number): string {
  return formatCreditsValue(creditValueCentsToCredits(value));
}

function SeedButton({ bearer }: { bearer: Record<string, string> | null }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');

  const seed = async () => {
    if (!bearer) return;
    setBusy(true);
    setResult('');
    try {
      const res = await fetch('/api/v1/admin/marketplace/seed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...bearer },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Seed failed');
      setResult(`Seeded ${json.seeded} items${json.errors ? `, ${json.errors} errors` : ''}`);
    } catch (err: any) {
      setResult(err.message || 'Seed failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ml-auto flex items-center gap-2">
      <button
        type="button"
        onClick={seed}
        disabled={busy || !bearer}
        className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold disabled:opacity-60"
      >
        {busy ? 'Seeding…' : 'Seed Marketplace'}
      </button>
      {result ? <span className="text-xs text-cocoa/70">{result}</span> : null}
    </div>
  );
}

export default function AdminMarketplace() {
  const bearer = useBearer();
  const [tab, setTab] = useState('catalog');
  const [sub, setSub] = useState('packs');
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [edit, setEdit] = useState<CatalogItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canLoad = Boolean(bearer);

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/admin/marketplace/catalog?tab=${sub}`, { headers: { ...bearer } });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.error || 'Failed to load catalog');
      }
      setItems(Array.isArray(json.items) ? json.items : []);
    } catch (err: unknown) {
      console.error('[admin-marketplace] load failed', err);
      setItems([]);
      setError((err as Error | null)?.message || 'Unable to load catalog');
    } finally {
      setLoading(false);
    }
  }, [bearer, canLoad, sub]);

  useEffect(() => {
    if (tab === 'catalog') {
      load();
    }
  }, [tab, sub, load]);

  const preparedItems = useMemo(() => items || [], [items]);

  async function save(row: CatalogItem) {
    if (!bearer) return;
    const payload: Record<string, unknown> = { ...row };

    if (sub === 'packs') {
      if (payload.usd_price !== undefined && payload.usd_price !== null && payload.usd_price !== '') {
        const price = Number.parseFloat(String(payload.usd_price));
        payload.usd_price = Number.isFinite(price) ? price : 0;
      }
      if (payload.credit_value_cents !== undefined && payload.credit_value_cents !== null) {
        payload.credit_value_cents = Math.round(Number(payload.credit_value_cents));
      }
    } else {
      if (payload.cost_cents !== undefined && payload.cost_cents !== null) {
        payload.cost_cents = Math.round(Number(payload.cost_cents));
      }
    }

    try {
      const res = await fetch('/api/v1/admin/marketplace/upsert', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...bearer },
        body: JSON.stringify({ tab: sub, row: payload }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.error || 'Save failed');
      }
      setEdit(null);
      await load();
    } catch (err: unknown) {
      console.error('[admin-marketplace] save failed', err);
      alert((err as Error | null)?.message || 'Save failed');
    }
  }

  return (
    <div className="min-h-screen bg-sand">
      <div className="container mx-auto p-4 pb-12 space-y-4">
        <a href="/portal/admin" className="text-sm underline text-cocoa">
          ← Admin
        </a>
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold text-cocoa">Marketplace</h1>
          <p className="text-sm text-cocoa/70">
            Manage digital catalog items, review credit activity, and support teachers with manual grants.
          </p>
        </header>

        <div className="flex flex-wrap gap-2">
          <TabButton active={tab === 'catalog'} onClick={() => setTab('catalog')}>
            Catalog
          </TabButton>
          <TabButton active={tab === 'catalog_v2'} onClick={() => setTab('catalog_v2')}>
            Catalog v2
          </TabButton>
          <TabButton active={tab === 'gifts'} onClick={() => setTab('gifts')}>
            Gifts &amp; Orders
          </TabButton>
          <TabButton active={tab === 'settings'} onClick={() => setTab('settings')}>
            Settings
          </TabButton>
          <SeedButton bearer={bearer} />
        </div>

        {tab === 'catalog' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <TabButton active={sub === 'packs'} onClick={() => setSub('packs')}>
                Credit Packs
              </TabButton>
              <TabButton active={sub === 'badges'} onClick={() => setSub('badges')}>
                Badges / Emblems
              </TabButton>
              <TabButton active={sub === 'avatars'} onClick={() => setSub('avatars')}>
                Avatars
              </TabButton>
              <button
                type="button"
                className="ml-auto px-3 py-2 bg-terracotta text-white rounded-lg"
                onClick={() =>
                  setEdit(
                    sub === 'packs'
                      ? { name: '', active: true, usd_price: 0, credit_value_cents: 0 }
                      : { name: '', active: true, cost_cents: 0 }
                  )
                }
              >
                + New
              </button>
            </div>

            {!bearer && <p className="text-sm text-cocoa/70">Authenticating…</p>}
            {error && <p className="text-sm text-brick">{error}</p>}
            {loading ? (
              <p className="text-sm text-cocoa/70">Loading…</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {preparedItems.map((item) => (
                  <div key={item.id || item.name} className="rounded-xl p-3 shadow bg-white border border-white/70">
                    <div className="flex items-center gap-3">
                      {sub !== 'packs' && item.image_url ? (
                        <img
                          src={item.image_url}
                          alt=""
                          className="w-16 h-16 rounded-lg object-cover border border-shell/80"
                        />
                      ) : null}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-cocoa truncate">{item.name}</div>
                        <div className="text-sm text-cocoa/70">
                          {sub === 'packs'
                            ? `$${Number(item.usd_price || 0).toFixed(2)} → ${formatCredits(
                                item.credit_value_cents || 0
                              )} credits (≈ ${formatUsd(creditValueCentsToUsd(item.credit_value_cents || 0))})`
                            : `${item.theme || 'default'}${item.tier ? ` • ${item.tier}` : ''} • cost ${formatCredits(
                                item.cost_cents || 0
                              )} credits`}
                        </div>
                        {item.active ? null : <div className="text-xs text-brick mt-1">Inactive</div>}
                      </div>
                      <button className="text-terracotta underline text-sm" onClick={() => setEdit(item)}>
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
                {!preparedItems.length && !loading && (
                  <div className="rounded-xl border border-dashed border-shell/80 bg-white/60 p-6 text-center text-sm text-cocoa/70">
                    No items yet.
                  </div>
                )}
              </div>
            )}

            {edit && (
              <EditDrawer
                sub={sub}
                item={edit}
                onClose={() => setEdit(null)}
                onSave={save}
              />
            )}
          </div>
        )}

        {tab === 'catalog_v2' && (
          <div className="rounded-xl p-4 bg-white shadow border border-white/70">
            <MarketItemsPanel bearer={bearer} />
          </div>
        )}

        {tab === 'gifts' && <GiftPanel bearer={bearer} />}

        {tab === 'settings' && (
          <div className="rounded-xl p-4 bg-white shadow border border-white/70 text-sm text-cocoa/70">
            No special settings yet. Coming soon: storefront themes and banner customization.
          </div>
        )}
      </div>
    </div>
  );
}

function EditDrawer({ sub, item, onClose, onSave }: EditDrawerProps) {
  const [draft, setDraft] = useState<CatalogItem>(() => ({ ...item }));

  useEffect(() => {
    setDraft({ ...item });
  }, [item]);

  const isPack = sub === 'packs';

  const handleChange = (key: keyof CatalogItem, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const creditValue = isPack
    ? typeof draft.credit_value_cents === 'number'
      ? creditValueCentsToCredits(draft.credit_value_cents)
      : ''
    : typeof draft.cost_cents === 'number'
      ? draft.cost_cents / 100
      : '';

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4">
      <div className="bg-white w-full max-w-xl rounded-2xl p-6 space-y-4 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-cocoa">
              {draft.id ? 'Edit' : 'New'} {isPack ? 'Credit Pack' : sub === 'badges' ? 'Badge' : 'Avatar'}
            </h2>
            <p className="text-sm text-cocoa/70">
              {isPack ? 'Set price and credit payout.' : 'Configure art, theme, and credit cost.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm text-cocoa/70 hover:text-cocoa">
            ✕
          </button>
        </div>

        <div className="grid gap-3">
          <label className="text-sm text-cocoa space-y-1">
            <span>Name</span>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={draft.name || ''}
              onChange={(event) => handleChange('name', event.target.value)}
              placeholder={isPack ? 'Support 75' : 'Harvest Glow'}
            />
          </label>

          {isPack ? (
            <>
              <label className="text-sm text-cocoa space-y-1">
                <span>USD Price</span>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded-lg px-3 py-2"
                  value={draft.usd_price ?? ''}
                  onChange={(event) => handleChange('usd_price', event.target.value)}
                />
              </label>
              <label className="text-sm text-cocoa space-y-1">
                <span>Credit Value (credits)</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="w-full border rounded-lg px-3 py-2"
                  value={creditValue}
                  onChange={(event) => {
                    const next = Math.round(Number(event.target.value || 0));
                    handleChange('credit_value_cents', Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </>
          ) : (
            <>
              <label className="text-sm text-cocoa space-y-1">
                <span>Image URL</span>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={draft.image_url || ''}
                  onChange={(event) => handleChange('image_url', event.target.value)}
                  placeholder="https://example.com/badge.png"
                />
              </label>
              <label className="text-sm text-cocoa space-y-1">
                <span>Theme</span>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={draft.theme || ''}
                  onChange={(event) => handleChange('theme', event.target.value)}
                  placeholder="default"
                />
              </label>
              <label className="text-sm text-cocoa space-y-1">
                <span>Tier</span>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={draft.tier || ''}
                  onChange={(event) => handleChange('tier', event.target.value)}
                  placeholder="bronze"
                />
              </label>
              <label className="text-sm text-cocoa space-y-1">
                <span>Cost (credits)</span>
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-2"
                  value={creditValue}
                  onChange={(event) => {
                    const next = Number(event.target.value || 0) * 100;
                    handleChange('cost_cents', Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </>
          )}

          {!isPack && (
            <label className="text-sm text-cocoa space-y-1">
              <span>Description</span>
              <textarea
                className="w-full border rounded-lg px-3 py-2"
                value={draft.description || ''}
                onChange={(event) => handleChange('description', event.target.value)}
                placeholder="Optional description"
              />
            </label>
          )}

          <label className="inline-flex items-center gap-2 text-sm text-cocoa">
            <input
              type="checkbox"
              checked={!!draft.active}
              onChange={(event) => handleChange('active', event.target.checked)}
            />
            Active
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="px-3 py-2 text-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-terracotta text-white text-sm"
            onClick={() => onSave(draft)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function GiftPanel({ bearer }: GiftPanelProps) {
  const [userId, setUserId] = useState('');
  const [credits, setCredits] = useState<number | string>(100);
  const [note, setNote] = useState('admin.grant');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState('');

  const canSubmit = bearer && userId && Number(credits) > 0 && !saving;

  const loadLedger = useCallback(async () => {
    if (!bearer) return;
    setLoadingLedger(true);
    setLedgerError('');
    try {
      const res = await fetch('/api/v1/admin/marketplace/ledger?limit=25', { headers: { ...bearer } });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Failed to load ledger');
      setLedger(Array.isArray(json.items) ? json.items : []);
    } catch (err: unknown) {
      console.error('[admin-marketplace] ledger load failed', err);
      setLedger([]);
      setLedgerError((err as Error | null)?.message || 'Unable to load ledger');
    } finally {
      setLoadingLedger(false);
    }
  }, [bearer]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const grant = useCallback(async () => {
    if (!bearer) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch('/api/v1/admin/credits/grant', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...bearer },
        body: JSON.stringify({ user_id: userId.trim(), credits: Number(credits), note }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Grant failed');
      setMessage(`Granted ${json.added || credits} credits. New balance: ${json.balance}`);
      setUserId('');
      setCredits(100);
      setNote('admin.grant');
      await loadLedger();
    } catch (err: unknown) {
      console.error('[admin-marketplace] grant failed', err);
      setMessage((err as Error | null)?.message || 'Grant failed');
    } finally {
      setSaving(false);
    }
  }, [bearer, credits, loadLedger, note, userId]);

  return (
    <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="rounded-xl p-4 bg-white shadow border border-white/70 space-y-3">
        <h2 className="text-lg font-semibold text-cocoa">Grant credits to a user</h2>
        <p className="text-sm text-cocoa/70">
          Enter the user&apos;s id (teacher or sponsor) and the number of credits to add to their balance.
        </p>
        <label className="text-sm text-cocoa space-y-1">
          <span>User ID</span>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </label>
        <label className="text-sm text-cocoa space-y-1">
          <span>Credits</span>
          <input
            type="number"
            min="1"
            className="w-full border rounded-lg px-3 py-2"
            value={credits}
            onChange={(event) => setCredits(event.target.value)}
          />
        </label>
        <label className="text-sm text-cocoa space-y-1">
          <span>Note</span>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={grant}
          className={`px-4 py-2 rounded-lg text-white text-sm font-semibold transition ${
            canSubmit ? 'bg-terracotta hover:bg-terracotta/90' : 'bg-shell text-cocoa/50 cursor-not-allowed'
          }`}
        >
          {saving ? 'Granting…' : 'Grant credits'}
        </button>
        {message && <p className="text-sm text-cocoa/80">{message}</p>}
      </div>

      <div className="rounded-xl p-4 bg-white shadow border border-white/70 space-y-3 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-cocoa">Recent ledger entries</h2>
          <button
            type="button"
            onClick={loadLedger}
            className="text-sm underline text-cocoa disabled:text-cocoa/40"
            disabled={loadingLedger || !bearer}
          >
            Refresh
          </button>
        </div>
        {ledgerError && <p className="text-sm text-brick">{ledgerError}</p>}
        {loadingLedger ? (
          <p className="text-sm text-cocoa/70">Loading ledger…</p>
        ) : ledger.length ? (
          <ul className="divide-y divide-shell/80 text-sm">
            {ledger.map((entry) => (
              <li key={entry.id} className="py-2">
                <div className="flex flex-wrap justify-between text-xs text-cocoa/60">
                  <span>{new Date(entry.created_at).toLocaleString()}</span>
                  <span className="uppercase tracking-wide">{entry.type}</span>
                </div>
                <div className="mt-1 text-cocoa font-medium">
                  +{Number(entry.delta_credits || 0).toLocaleString()} credits → {entry.user_id || entry.account_id}
                </div>
                {entry.note ? <div className="text-xs text-cocoa/70">{entry.note}</div> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-cocoa/70">No ledger entries yet.</p>
        )}
      </div>
    </div>
  );
}
